import { Injectable } from "@angular/core";
import { PatternBead } from "src/app/models/pattern/pattern-bead";
import { PaletteDetailModel } from "src/app/models/palettes/palette_detail_model";
import { StitchTypeEnum } from "src/app/models/stitchs/stitch_model";
import { EMPTY_BEAD } from "src/app/helpers/constants";
import {
  WordChartResult,
  WordChartRow,
  WordChartPaletteEntry,
  BeadSegment,
} from "src/app/models/word-chart/word-chart-models";

interface LegendEntry extends WordChartPaletteEntry {}

@Injectable({
  providedIn: "root",
})
export class WordChartService {
  buildWordChart(pattern: PatternBead): WordChartResult {
    const beadsGrid = pattern?.grid || [];
    const stitchId = pattern?.stitchId;

    if (!beadsGrid.length || !beadsGrid[0]?.length) {
      return { rows: [], legend: [] };
    }

    const legendMap = this.buildLegend(beadsGrid);
    const rows = this.buildRows(beadsGrid, stitchId, legendMap);
    const legend = Array.from(legendMap.values());

    return { rows, legend };
  }

  private buildLegend(beadsGrid: PaletteDetailModel[][]): Map<string, LegendEntry> {
    const legendMap = new Map<string, LegendEntry>();
    let currentLetter = "A";

    for (let nRow = 0; nRow < beadsGrid.length; nRow++) {
      for (let nCol = 0; nCol < beadsGrid[0].length; nCol++) {
        const bead = this.getBeadGridCell(beadsGrid, nRow, nCol);

        if (bead && bead.paletteDetailId !== -1) {
          if (!legendMap.has(bead.name)) {
            legendMap.set(bead.name, {
              letter: currentLetter,
              beadName: bead.name,
              beadColor: bead.color,
              description: bead.description || "",
              count: 0,
              paletteDetailId: bead.paletteDetailId,
              paletteTypeId: bead.paletteTypeId,
              paletteId: bead.paletteId,
            });
            currentLetter = this.nextChar(currentLetter);
          }

          const entry = legendMap.get(bead.name);
          entry.count++;
        }
      }
    }

    return legendMap;
  }

  private buildRows(
    beadsGrid: PaletteDetailModel[][],
    stitchId: StitchTypeEnum,
    legendMap: Map<string, LegendEntry>,
  ): WordChartRow[] {
    const rows: WordChartRow[] = [];

    let side: "L" | "R" = "L";

    for (let nRow = 0; nRow < beadsGrid.length; nRow++) {
      if (stitchId === StitchTypeEnum.Peyote) {
        if (nRow === 0) {
          const rowHeader = "Row 1&2 (L):";
          const segments = this.buildRowSegments(
            beadsGrid,
            nRow,
            side,
            legendMap,
            1,
            0,
          );
          rows.push({ rowHeader, direction: side, rowIndex: nRow, segments });
        } else {
          const firstHeader = `Row ${nRow * 2 + 1} (${side}):`;
          const firstSegments = this.buildRowSegments(
            beadsGrid,
            nRow,
            side,
            legendMap,
            2,
            0,
          );
          rows.push({
            rowHeader: firstHeader,
            direction: side,
            rowIndex: nRow,
            segments: firstSegments,
          });

          side = side === "L" ? "R" : "L";

          const startNCol = beadsGrid[0].length % 2 === 0 ? 0 : 1;
          const secondHeader = `Row ${nRow * 2 + 2} (${side}):`;
          const secondSegments = this.buildRowSegments(
            beadsGrid,
            nRow,
            side,
            legendMap,
            2,
            startNCol,
          );
          rows.push({
            rowHeader: secondHeader,
            direction: side,
            rowIndex: nRow,
            segments: secondSegments,
          });
        }
      } else {
        const rowHeader = `Row ${nRow + 1} (${side}):`;
        const segments = this.buildRowSegments(
          beadsGrid,
          nRow,
          side,
          legendMap,
          1,
          0,
        );
        rows.push({ rowHeader, direction: side, rowIndex: nRow, segments });
      }

      side = side === "L" ? "R" : "L";
    }

    return rows;
  }

  private buildRowSegments(
    beadsGrid: PaletteDetailModel[][],
    nRow: number,
    side: "L" | "R",
    legendMap: Map<string, LegendEntry>,
    step: number,
    startNCol: number,
  ): BeadSegment[] {
    const segments: BeadSegment[] = [];
    let lastBeadLetter = "";
    let countBeads = 0;

    for (let nCol = startNCol; nCol < beadsGrid[0].length; nCol += step) {
      let bead = null;

      if (side === "L") {
        bead = this.getBeadGridCell(beadsGrid, nRow, nCol);
      } else {
        bead = this.getBeadGridCell(
          beadsGrid,
          nRow,
          beadsGrid[0].length - nCol - 1,
        );
      }

      let letter = "_";
      if (bead.paletteDetailId !== -1) {
        const entry = legendMap.get(bead.name);
        if (entry) {
          letter = entry.letter;
        }
      }

      if (lastBeadLetter !== "" && lastBeadLetter !== letter) {
        const segmentBead = this.getLegendByLetter(lastBeadLetter, legendMap);
        segments.push({
          letter: lastBeadLetter,
          count: countBeads,
          beadName: segmentBead?.beadName || "Empty",
          beadColor: segmentBead?.beadColor || "000000",
          description: segmentBead?.description || "",
          paletteDetailId: segmentBead?.paletteDetailId ?? -1,
          paletteTypeId: segmentBead?.paletteTypeId ?? 0,
          paletteId: segmentBead?.paletteId ?? -1,
        });
        countBeads = 1;
      } else {
        countBeads++;
      }

      lastBeadLetter = letter;
    }

    if (countBeads > 0) {
      const lastBead = this.getLegendByLetter(lastBeadLetter, legendMap);
      segments.push({
        letter: lastBeadLetter,
        count: countBeads,
        beadName: lastBead?.beadName || "Empty",
        beadColor: lastBead?.beadColor || "000000",
        description: lastBead?.description || "",
        paletteDetailId: lastBead?.paletteDetailId ?? -1,
        paletteTypeId: lastBead?.paletteTypeId ?? 0,
        paletteId: lastBead?.paletteId ?? -1,
      });
    }

    return segments;
  }

  private getLegendByLetter(
    letter: string,
    legendMap: Map<string, LegendEntry>,
  ): LegendEntry | null {
    if (letter === "_") return null;
    for (const entry of legendMap.values()) {
      if (entry.letter === letter) return entry;
    }
    return null;
  }

  private getBeadGridCell(
    beadsGrid: PaletteDetailModel[][],
    nRow: number,
    nCol: number,
  ): PaletteDetailModel {
    const pd = beadsGrid[nRow][nCol];
    return pd || EMPTY_BEAD;
  }

  private nextChar(c: string): string {
    if (c.length === 1) {
      if (c === "Z") {
        return "A2";
      }
      return String.fromCharCode(c.charCodeAt(0) + 1);
    }

    const number = parseInt(c[1], 10);
    if (c[0] === "Z") {
      return `A${number + 1}`;
    }
    return String.fromCharCode(c.charCodeAt(0) + 1) + number;
  }
}
